import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { scheduleUndoableDelete } from "@/lib/undoDelete";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, PencilSimple, Trash, MapPin } from "@phosphor-icons/react";

export default function Districts() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ kode: "", nama: "", aktif: true });

  const load = async () => {
    const { data } = await api.get("/districts");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ kode: "", nama: "", aktif: true });
    setOpen(true);
  };
  const openEdit = (d) => {
    setEditing(d);
    setForm({ kode: d.kode, nama: d.nama, aktif: d.aktif });
    setOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/districts/${editing.id}`, form);
        toast.success("Distrik diperbarui");
      } else {
        await api.post("/districts", form);
        toast.success("Distrik ditambahkan");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onDelete = (d) => {
    scheduleUndoableDelete({
      items, setItems, target: d,
      label: `Distrik ${d.kode}`,
      apiCall: () => api.delete(`/districts/${d.id}`, { params: { hard: true } }),
    });
  };

  const filtered = items.filter((d) => !q || d.kode.includes(q) || d.nama.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-6 md:p-8 max-w-5xl">
      <div className="flex flex-col md:flex-row md:items-end justify-between border-b border-border pb-6 mb-6 gap-4">
        <div>
          <div className="overline">Master Data</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2 flex items-center gap-3">
            <span className="w-11 h-11 rounded-full bg-orange-100 text-primary flex items-center justify-center shrink-0">
              <MapPin size={22} weight="bold" />
            </span>
            Distrik
          </h1>
        </div>
        <Button onClick={openNew} className="rounded-lg font-bold uppercase tracking-wider" data-testid="add-district-button">
          <Plus size={16} className="mr-2" /> Tambah Distrik
        </Button>
      </div>

      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari kode atau nama..." className="rounded-lg h-11 mb-4" data-testid="search-district-input" />

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-secondary/50 text-left">
            <tr>
              <th className="p-3 overline">Kode</th>
              <th className="p-3 overline">Nama Distrik</th>
              <th className="p-3 overline">Status</th>
              <th className="p-3 overline">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((d) => (
              <tr key={d.id} className="border-b border-border hover:bg-secondary/50">
                <td className="p-3 font-mono font-bold">{d.kode}</td>
                <td className="p-3">{d.nama}</td>
                <td className="p-3">
                  <span className={`status-pill ${d.aktif ? "status-termuat" : "status-belum"}`}>
                    {d.aktif ? "AKTIF" : "NONAKTIF"}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" className="rounded-lg" onClick={() => openEdit(d)} data-testid={`edit-district-${d.kode}`}>
                      <PencilSimple size={14} />
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-lg text-destructive bg-destructive/5 border-destructive/40" onClick={() => onDelete(d)} data-testid={`delete-district-${d.kode}`} title="Hapus permanen">
                      <Trash size={14} weight="fill" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">Belum ada distrik.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-lg max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Distrik" : "Tambah Distrik"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="district-form">
            <div>
              <Label className="overline block mb-2">Kode Distrik *</Label>
              <Input value={form.kode} onChange={(e) => setForm((f) => ({ ...f, kode: e.target.value }))} required className="rounded-lg h-11 font-mono" data-testid="input-district-kode" />
            </div>
            <div>
              <Label className="overline block mb-2">Nama Distrik *</Label>
              <Input value={form.nama} onChange={(e) => setForm((f) => ({ ...f, nama: e.target.value.toUpperCase() }))} required className="rounded-lg h-11" data-testid="input-district-nama" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.aktif} onCheckedChange={(v) => setForm((f) => ({ ...f, aktif: v }))} data-testid="switch-district-aktif" />
              <Label>Aktif</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-lg" onClick={() => setOpen(false)}>Batal</Button>
              <Button type="submit" className="rounded-lg font-bold uppercase" data-testid="save-district-button">Simpan</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
